import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { uidFactory } from '@10000/common'
import { Body, Box, Dialog, Input } from '..'
import './DropDown.scss'

const uid = uidFactory()

export const DropDown = ({ id: idProp, value, options, onChange, onInputChange, onInputBlur, dark, search, fullWidth, required, ...props }) => {
  const id = useMemo(() => idProp || uid(), [idProp])
  const dropDownRef = useRef()
  const listRef = useRef()
  const mobileListRef = useRef()

  const [searching, setSearching] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [focus, setFocus] = useState(false)

  // Set initial search value or reset when search value is 'incomplete' when unfocused
  useEffect(() => {
    if((value !== undefined && value !== '') && !focus && (!searchValue || options.findIndex(option => option.value === searchValue) === -1)){
      setSearchValue(options[options.findIndex(option => option.value === value)]?.label)
    } else if(search && value === '' && !focus) {
      setSearchValue('')
    }
  }, [search, focus, searchValue, value, options])

  // Create unique list as some options provided have duplicates i.e. countries
  const formatOptions = useCallback(() =>
    (search && searchValue !== '' && searching)
      ? Array.from(
        new Map(
          options.filter(item => item.label.toLowerCase().includes(searchValue.toLowerCase()))
            .map(item => [item.label, item])
        ).values()
      )
      : options
  , [searchValue, searching, options, search])

  const [focusedOption, setFocusedOption] = useState((value !== '' && value !== undefined)
    ? formatOptions().findIndex(item => item.value === value)
    : 0
  )

  const focusAndScrollToOption = useCallback(index => {
    setFocusedOption(index)
    document.getElementById(`${id}-option-${index}`)?.scrollIntoView({ block: "nearest", inline: "nearest" })
    document.getElementById(`${id}-option-mobile-${index}`)?.scrollIntoView({ block: "nearest", inline: "nearest" })
  }, [id])

  const onItemSelected = useCallback((item, stopFocus, updateSearch) => {
    // List option can have no value and label act as a break i.e. for common countries
    if(item?.value !== undefined && item?.value !== ''){
      if(!searching || updateSearch){
        setSearchValue(item.label)
      } else {
        setFocusedOption(formatOptions().findIndex(x => x.value === item.value))
      }
      if(onChange){
        onChange(item.value)
      }
    }

    if(stopFocus){
      setSearching(false)
      setFocusedOption(options.findIndex(x => x.value === item.value))
      setFocus(false)
    }
  }, [searching, formatOptions, options, onChange])

  const handleKeyDown = useCallback(e => {
    if(focus){
      if(e.key === 'Tab') {
        onItemSelected(formatOptions()[focusedOption], true, true)
      } else if(e.key === 'Enter') {
        e.preventDefault() // Enter key pressed within form will trigger submit
        onItemSelected(formatOptions()[focusedOption], true, true)
      } else if(e.key ==='ArrowDown' && focusedOption < formatOptions().length - 1) {
        e.preventDefault() // Arrow key will trigger page scrolling
        focusAndScrollToOption(focusedOption + 1)
        onItemSelected(formatOptions()[focusedOption + 1], false, false)
      } else if(e.key ==='ArrowUp' && focusedOption > 0) {
        e.preventDefault() // Arrow key will trigger page scrolling
        focusAndScrollToOption(focusedOption - 1)
        onItemSelected(formatOptions()[focusedOption - 1], false, false)
      }
    }
  }, [focus, focusedOption, focusAndScrollToOption, formatOptions, onItemSelected])

  const handleClickOutside = useCallback(e => {
    if ((listRef.current && !listRef.current?.contains(e.target))
      && (mobileListRef.current && !mobileListRef.current?.contains(e.target))
      && (dropDownRef.current && !dropDownRef.current?.contains(e.target))) {
      setFocus(false)
      setSearching(false)
      if(onInputBlur){
        onInputBlur()
      }
    }
  }, [listRef, mobileListRef, dropDownRef, onInputBlur])

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown)
    document.addEventListener("mousedown", handleClickOutside)

    return () => {
      document.removeEventListener("keydown", handleKeyDown)
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [handleKeyDown, handleClickOutside])

  // Reset the option's focus when the dropdown is re-focused
  useEffect(() => {
    function getScrollParent(node, index) {
      const style = getComputedStyle(node)
      if((style.overflowY === 'auto' || style.overflowY === 'scroll') && index > 0) {
        return node
      } else {
        return getScrollParent(node.parentNode, index + 1)
      }
    }

    const scrollListIntoViewport = el => {
      const scrollNode = getScrollParent(el, 0)
      const { bottom } = el.getBoundingClientRect()
      if(bottom > scrollNode.getBoundingClientRect().bottom){
        el.scrollIntoView({ block: "end", inline: "nearest" })
        scrollNode.scrollBy(0, 3) //For dropdown list box-shadow effect
      }
    }

    if(focus && listRef.current) {
      scrollListIntoViewport(listRef.current)
      focusAndScrollToOption(focusedOption)
    }
  }, [focus, focusedOption, focusAndScrollToOption, options])

  const onSearch = e => {
    if(onInputChange) {
      onInputChange(e.target.value)
    }
    if(search){
      setSearchValue(e.target.value)
      setSearching(true)
      setFocus(true)
      setFocusedOption(0)
    }
  }

  const handleMobileFocus = () => {
    setFocus(true)
    document.getElementById(id + '-mobile-dropdown')?.focus()
  }

  const fullProps = {
    value: searchValue,
    onChange: onSearch,
    dark,
    rightIcon: "ChevronDown",
    required,
    ...props
  }

  return <Box column fullWidth={fullWidth}>
    <Box column gap={6}>
      <Box
        className="desktop-only"
        ref={dropDownRef}
        fullWidth
        column
        style={{
          caretColor: !search && 'transparent'
        }}
      >
        <Input
          id={id}
          selectTextOnFocus={search}
          onInputFocus={() => setFocus(true)}
          autoComplete="off"
          {...fullProps}
        />
      </Box>
      <Box
        className="mobile-only"
        fullWidth
        column
      >
        <Input
          id={id + '-mobile'}
          readOnly
          onInputFocus={handleMobileFocus}
          autoComplete="off"
          {...fullProps}
        />
      </Box>
    </Box>

    {(focus && formatOptions().length > 0) &&
      <DesktopPopOverDropDown
        id={id}
        ref={listRef}
        options={formatOptions()}
        focusedOption={focusedOption}
        onSelect={onItemSelected}
        dark={dark}
      />
    }
    <MobileDialogDropDown
      id={id}
      open={focus}
      options={formatOptions()}
      focusedOption={focusedOption}
      onSelect={onItemSelected}
      search={search}
      ref={mobileListRef}
    >
      {search && (
        <Input
          id={id + '-mobile-dropdown'}
          value={searchValue}
          onChange={onSearch}
          dark
          selectTextOnFocus
          autoComplete="off"
          {...props}
        />
      )}
    </MobileDialogDropDown>
  </Box>
}

const DesktopPopOverDropDown = forwardRef(function DesktopPopOverDropDown({ id, options, focusedOption, onSelect, dark }, ref) {
  return (
    <Box className="desktop-only" position="relative">
      <Box ref={ref} column className={`drop-down-list${dark ? ' dark' : ' black-scroller'}`}>
        <Box column className="drop-down-list-scroll">
         {options.map((item, index) =>
            <Box key={index} className="drop-down-spaced-container" fullWidth>
              <Box
                id={`${id}-option-${index}`}
                fullWidth
                className={`drop-down-list-item${focusedOption === index ? ' focus-item' : ''}`}
                onClick={() => onSelect(item, true, true)}
              >
                {item.label}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
})

const MobileDialogDropDown = forwardRef(function MobileDialogDropDown({ id, open, options, focusedOption, onSelect, search, children }, ref) {
  return (
    <Dialog open={open} className="mobile-only" mobileBackground="white" showControls={false}>
      <Box ref={ref} height={search ? '80dvh' : 250} column fullWidth gap={16}>
        {children}
        <Box column overflowY="auto" gap={8} fullWidth height={search && 200}>
          {options.map((item, index) =>
            <Box
              key={index}
              id={`${id}-option-mobile-${index}`}
              className={`mobile-drop-down-option${focusedOption === index ? ' focus-item' : ''}`}
              onClick={() => onSelect(item, true, true)}
            >
              <Body size={16}>{item.label}</Body>
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  )
})